import type { OrderStatusType, PaymentStatusType } from '~/services/order.service'
import type { MainColorsType } from '~/types/components'

/**
 * Vocabulario de estados y tipos de movimiento, en un solo sitio.
 *
 * Antes cada pantalla definía sus propios mapas de labels y colores (listados,
 * formularios y dashboard los copiaban): añadir un estado nuevo exigía tocarlos
 * todos, y un label cambiado en una sola página confundía al usuario entre el
 * listado y el editor.
 */

// ── Estados de cotización ────────────────────────────────────────────────────

export const quoteStatusLabels: Record<string, string> = {
  draft: 'Borrador',
  sent: 'Enviada',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  expired: 'Expirada',
  cancelled: 'Cancelada'
}

export const quoteStatusColors: Record<string, MainColorsType> = {
  draft: 'default',
  sent: 'info',
  approved: 'success',
  rejected: 'danger',
  expired: 'warning',
  cancelled: 'danger'
}

// ── Estados de cuenta de cobro ───────────────────────────────────────────────

export const accountStatusLabels: Record<string, string> = {
  draft: 'Borrador',
  sent: 'Enviada',
  paid: 'Pagada',
  overdue: 'Vencida'
}

export const accountStatusColors: Record<string, MainColorsType> = {
  draft: 'default',
  sent: 'info',
  paid: 'success',
  overdue: 'danger'
}

// ── Estados de pedido ────────────────────────────────────────────────────────

export const orderStatusLabels: Record<OrderStatusType, string> = {
  pending: 'Pendiente de pago',
  paid: 'Pagado',
  cancelled: 'Cancelado',
  refunded: 'Reembolsado',
  expired: 'Expirado'
}

export const orderStatusColors: Record<OrderStatusType, MainColorsType> = {
  pending: 'warning',
  paid: 'success',
  cancelled: 'default',
  refunded: 'info',
  expired: 'default'
}

// ── Estados de un intento de cobro ───────────────────────────────────────────

export const paymentStatusLabels: Record<PaymentStatusType, string> = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  refunded: 'Reembolsado',
  cancelled: 'Cancelado'
}

export const paymentStatusColors: Record<PaymentStatusType, MainColorsType> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  refunded: 'info',
  cancelled: 'default'
}

// ── Tipos de movimiento de inventario ────────────────────────────────────────

export const movementLabel: Record<string, string> = {
  in: 'Entrada',
  out: 'Salida',
  adjust: 'Ajuste'
}

export const movementColor: Record<string, MainColorsType> = {
  in: 'success',
  out: 'danger',
  adjust: 'warning'
}

// ── Tamaño de archivo ─────────────────────────────────────────────────────────

/**
 * "1.5 MB", "210 KB": fuente única de formato de bytes. Antes había tres copias
 * divergentes (una truncaba en MB, otra sin GB, otra con `1.0 KB`).
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1)
  return `${(bytes / k ** i).toFixed(1)} ${sizes[i]}`
}
